import { closeDrawer, isOpenInfoModal, isReportDrawerOpen, reportFormDataInnit, reportListData, selectedInfoModalTab } from 'Pages/ReportsAlerts/Reports/@signals'
import { INFO_MODAL_TABS } from 'Pages/ReportsAlerts/Reports/@constants'

// ─── Modal ranges ─────────────────────────────────────────────────────────────
// Steps 3-19 require the report creation drawer to be open.
export const MODAL_RANGES = [
	{
		from: 3, to: 19,
		open: () => reportFormDataInnit(),
		close: () => closeDrawer(),
		isOpen: () => isReportDrawerOpen.value === true,
	},
	// Steps 33-35 require the Info Report modal to be open.
	{
		from: 33, to: 35,
		open: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		close: () => { isOpenInfoModal.value = null },
		isOpen: () => !!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root'),
	},
]

// ─── DOM helpers ────────────────────────────────────────────────────────────

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findTableColumn(headerText) {
	const headers = document.querySelectorAll('th, [class*="MUIDataTableHeadCell"]')
	for (const h of headers) {
		if (h.textContent.trim().toLowerCase().includes(headerText.toLowerCase())) {
			return h
		}
	}
	return null
}

// ─── Language detection ──────────────────────────────────────────────────────

const ES = navigator.language?.startsWith('es')

// Close drawer on tour skip/close during drawer section
const drawerCleanup = () => closeDrawer()

// ─── Steps ───────────────────────────────────────────────────────────────────

export const TOUR_STEPS = [
	// ─── 0: Intro ───────────────────────────────────────────────────────────────
	{
		type: 'intro',
		icon: 'star',
		title: ES ? 'Bienvenido a Informes Rating' : 'Welcome to Rating Reports',
		description: ES
			? 'Los Informes Rating monitorizan la posicion de tus precios frente a la competencia en sitios directos. Configura busquedas periodicas y analiza tu rating de precios para estar siempre competitivo.'
			: 'Rating Reports monitor your price position against competitors on direct sites. Set up periodic searches and analyze your price rating to stay competitive.',
	},
	// ─── 1: Icons Legend ────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Rating Analyzer')
			if (!el) return null
			let parent = el.parentElement
			for (let i = 0; i < 6; i++) {
				if (!parent) break
				const text = parent.textContent.toLowerCase()
				if (text.includes('rating analyzer') && text.includes('real time')) {
					if (parent.children.length >= 2) return parent
				}
				parent = parent.parentElement
			}
			return el.parentElement
		},
		icon: 'icons',
		title: ES ? 'Leyenda de Iconos' : 'Icons Legend',
		description: ES
			? 'La leyenda muestra los iconos disponibles en la columna Fecha. Los informes rating tienen dos acciones: acceder al Rating Analyzer para analizar tu posicion de precios, y ver el historico en tiempo real.'
			: 'The legend shows icons available in the Date column. Rating reports have two actions: access the Rating Analyzer to analyze your price position, and view the real-time historical data.',
		example: ES
			? 'Rating Analyzer | Historico en Tiempo Real'
			: 'Rating Analyzer | Real Time Historical',
	},
	// ─── 2: Create Rating Report — opens the drawer ──────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const btn = findByText('Create Report')
			return btn?.closest('button') || btn?.parentElement
		},
		icon: 'file-plus',
		title: ES ? 'Crear Informe Rating' : 'Create Rating Report',
		description: ES
			? 'Este boton abre el formulario de creacion configurado para informes de rating. Haz clic en "Abrir Formulario" y recorreremos cada campo juntos: nombre, sitios, oficinas, fechas, filtros y frecuencia.'
			: 'This button opens the creation form configured for rating reports. Click "Open Form" and we will walk through each field together: name, sites, offices, dates, filters, and frequency.',
		action: () => reportFormDataInnit(),
		waitFor: () => isReportDrawerOpen.value === true,
	},
	// ─── 3: Drawer overview ─────────────────────────────────────────────────────
	{
		type: 'intro',
		icon: 'rectangle-list',
		title: ES ? 'Formulario de Creacion' : 'Creation Form',
		description: ES
			? 'El formulario tiene cinco secciones: Nombre, Sitios y Oficinas, Configuracion de Busqueda, Filtros de Exportacion y Frecuencia. Vamos campo por campo.'
			: 'The form has five sections: Name, Sites & Offices, Search Configuration, Export Filters, and Frequency. Let\'s go field by field.',
		reverseAction: () => closeDrawer(),
		cleanup: drawerCleanup,
	},
	// ─── 4: Num. Searches info button ────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-num-searches-info"]'),
		icon: 'circle-info',
		title: ES ? 'Informacion de Busquedas' : 'Search Info',
		description: ES
			? 'El indicador "Num. Searches" muestra el total de busquedas que ejecutara este informe. Pulsa el icono para ver el desglose detallado: numero de oficinas, busquedas por oficina, fechas de recogida primera y ultima, y el listado completo de fechas de pickup.'
			: 'The "Num. Searches" indicator shows the total searches this report will execute. Click the info icon to see the detailed breakdown: number of offices, searches per office, first and last pickup dates, and the full pickup date list.',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 1 — Report Name
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 5: Report Name field ───────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-report-name"]'),
		icon: 'pen-line',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre que identifica este informe en la lista. Usa nombres descriptivos que incluyan la oficina o mercado, por ejemplo "Madrid Airport Rating Weekly". Minimo 5 caracteres, maximo 100.'
			: 'The name that identifies this report in the list. Use descriptive names that include the office or market, e.g. "Madrid Airport Rating Weekly". Minimum 5 characters, maximum 100.',
		example: ES ? 'Ej: Madrid Airport Rating Weekly' : 'E.g.: Madrid Airport Rating Weekly',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 2 — Sites & Offices
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 6: Use Office Group switch ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-switch"]'),
		icon: 'toggle-off',
		title: ES ? 'Modo de Oficina' : 'Office Mode',
		description: ES
			? 'Elige como seleccionar las oficinas de recogida. Desactivado: seleccionas oficinas individuales. Activado: usas un grupo de oficinas predefinido que cubre varias ubicaciones. Los grupos son mas practicos para monitorizar mercados completos.'
			: 'Choose how to select pickup offices. Off: pick individual offices. On: use a predefined office group covering multiple locations. Groups are more practical for monitoring entire markets.',
		example: ES ? 'Activado = Grupo | Desactivado = Oficinas individuales' : 'On = Group | Off = Individual offices',
		cleanup: drawerCleanup,
	},
	// ─── 7: Pickup Office / Group Office ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-field"]'),
		icon: 'building',
		title: ES ? 'Oficinas de Recogida' : 'Pickup Offices',
		description: ES
			? 'Las oficinas (o el grupo) de recogida que quieres monitorizar para el rating. El numero de oficinas multiplicado por sitios y fechas determina el total de busquedas.'
			: 'The pickup offices (or group) you want to monitor for rating. The number of offices multiplied by sites and dates determines the total searches.',
		cleanup: drawerCleanup,
	},
	// ─── 8: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-site"]'),
		icon: 'globe',
		title: ES ? 'Sitios Web' : 'Sites',
		description: ES
			? 'Las webs de alquiler de coches que quieres monitorizar para el rating de precios. Aqui solo se muestran sitios directos — los sitios de brokers no son relevantes para el analisis de rating.'
			: 'The car rental websites to monitor for price rating. Only direct sites are shown here — broker sites are not relevant for rating analysis.',
		cleanup: drawerCleanup,
	},
	// ─── 9: Currency ────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-currency"]'),
		icon: 'euro-sign',
		title: ES ? 'Moneda' : 'Currency',
		description: ES
			? 'La moneda en la que se mostraran los precios en los resultados del informe. Selecciona la moneda que corresponda al mercado que estas monitorizando.'
			: 'The currency in which prices will be shown in the report results. Select the currency that matches the market you are monitoring.',
		cleanup: drawerCleanup,
	},
	// ─── 10: Dropoff Office (optional) ───────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-dropoff"]'),
		icon: 'building-flag',
		title: ES ? 'Oficina de Devolucion (Opcional)' : 'Dropoff Office (Optional)',
		description: ES
			? 'Oficina de devolucion opcional para el informe de rating. Si no se especifica, las busquedas seran de ida y vuelta en la misma oficina. Especificar una oficina de devolucion diferente permite monitorizar rutas de solo ida en el analisis de rating.'
			: 'Optional dropoff office for the rating report. If not specified, searches will be round-trip at the same office. Specifying a different dropoff office allows monitoring one-way routes in the rating analysis.',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 3 — Search Configuration
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 11: Use CSV switch ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-csv-switch"]'),
		icon: 'file-csv',
		title: ES ? 'Modo de Busqueda: Fecha o CSV' : 'Search Mode: Date or CSV',
		description: ES
			? 'Por defecto el informe genera busquedas a partir de la configuracion de fechas. Si activas "Use CSV", puedes subir un fichero con busquedas completamente personalizadas — util para combinaciones especificas que no caben en la configuracion estandar.'
			: 'By default the report generates searches from the date configuration. If you enable "Use CSV", you can upload a file with fully custom searches — useful for specific combinations that do not fit the standard configuration.',
		cleanup: drawerCleanup,
	},
	// ─── 12: Pickup Dates mode selector ─────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-pickup-dates"]'),
		icon: 'calendar-range',
		title: ES ? 'Modo de Fechas de Recogida' : 'Pickup Dates Mode',
		description: ES
			? 'Define como se seleccionan las fechas de recogida. Cuatro opciones: dias especificos, lead times (x dias desde hoy), dia y semana, o proximo mes completo. Cada modo genera un subformulario diferente adaptado al tipo de monitorizacion de rating.'
			: 'Defines how pickup dates are selected. Four options: specific days, lead times (x days from today), day and week, or next full month. Each mode shows a different sub-form adapted to the rating monitoring type.',
		example: ES
			? 'Lead times: 1,3,7,14,30 dias | Especificos: 15/06, 22/06...'
			: 'Lead times: 1,3,7,14,30 days | Specific: 15/06, 22/06...',
		cleanup: drawerCleanup,
	},
	// ─── 13: Rental length ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-rental-length"]'),
		icon: 'moon',
		title: ES ? 'Duracion del Alquiler' : 'Rental Length',
		description: ES
			? 'El numero de noches del alquiler que se buscaran. Puedes seleccionar varias duraciones a la vez. Cada duracion combinada con cada fecha genera una busqueda independiente para el analisis de rating.'
			: 'The number of rental nights to search. You can select multiple durations at once. Each duration combined with each date generates an independent search for the rating analysis.',
		example: ES ? 'Ej: 1, 3, 7 noches → 3x busquedas por fecha' : 'E.g.: 1, 3, 7 nights → 3x searches per date',
		cleanup: drawerCleanup,
	},
	// ─── 14: Days to monitor ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-days-monitor"]'),
		icon: 'calendar-days',
		title: ES ? 'Dias a Monitorizar' : 'Days to Monitor',
		description: ES
			? 'Cuantos dias consecutivos a partir de hoy incluira el informe. Por ejemplo, 30 dias significa que se buscaran precios para los proximos 30 dias de recogida. Maximo 365 dias.'
			: 'How many consecutive days from today the report will include. For example, 30 days means prices will be searched for the next 30 pickup days. Maximum 365 days.',
		cleanup: drawerCleanup,
	},
	// ─── 15: Pickup Time / Dropoff Time ─────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-pickup-time"]'),
		icon: 'clock',
		title: ES ? 'Hora de Recogida y Devolucion' : 'Pickup & Dropoff Time',
		description: ES
			? 'La hora de recogida y la hora de devolucion que se usaran en las busquedas de rating. Configuralas para obtener precios comparables entre todos los sitios monitorizados.'
			: 'The pickup time and dropoff time used in rating searches. Configure them to get comparable prices across all monitored sites.',
		example: ES ? 'Recogida: 10:00 | Devolucion: 10:00' : 'Pickup: 10:00 | Dropoff: 10:00',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 4 — Export Filters (optional)
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 16: Export Filters section ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-export-filters"]'),
		icon: 'sliders',
		title: ES ? 'Filtros de Exportacion (Opcional)' : 'Export Filters (Optional)',
		description: ES
			? 'Seccion opcional para filtrar los resultados. Puedes establecer un limite de horas y activar la generacion automatica del informe tras cada ejecucion. Los informes rating tienen filtros reducidos respecto a otros tipos.'
			: 'Optional section to filter results. You can set an hours limit and enable automatic report generation after each execution. Rating reports have fewer filter options compared to other report types.',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 5 — Frequency & Notification
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 17: Email ──────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-email"]'),
		icon: 'envelope',
		title: ES ? 'Email de Notificacion' : 'Notification Email',
		description: ES
			? 'Los correos electronicos a los que se enviaran los resultados del informe cuando se complete la ejecucion. Puedes anadir varios emails — pulsa Enter o escribe una coma entre cada uno. Por defecto se rellena con tu email de usuario.'
			: 'The email addresses that will receive report results when execution completes. You can add multiple emails — press Enter or type a comma between each. Defaults to your user email.',
		cleanup: drawerCleanup,
	},
	// ─── 18: Execution frequency (hours) ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-exec-frequency"]'),
		icon: 'clock-rotate-left',
		title: ES ? 'Horas de Ejecucion' : 'Execution Hours',
		description: ES
			? 'Las horas del dia en las que se ejecutara automaticamente el informe de rating. Una o dos comprobaciones diarias suelen ser suficientes para monitorizar el rating de precios con precision.'
			: 'The times of day when the rating report will run automatically. One or two daily checks are usually sufficient to accurately monitor price rating.',
		example: ES ? 'Ej: 08:00, 18:00 → dos capturas al dia' : 'E.g.: 08:00, 18:00 → two daily snapshots',
		cleanup: drawerCleanup,
	},
	// ─── 19: Frequencies (days) + close drawer ───────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-frequencies"]'),
		icon: 'calendar-week',
		title: ES ? 'Dias de Ejecucion' : 'Execution Days',
		description: ES
			? 'Los dias de la semana en los que se ejecutara el informe. Combinado con las horas de ejecucion define el calendario completo. Cuando lo tengas todo configurado, guarda con el boton "Save" del pie del formulario.'
			: 'The days of the week when the report will run. Combined with execution hours this defines the complete schedule. When everything is configured, save using the "Save" button at the bottom of the form.',
		example: ES ? 'Lunes, Miercoles, Viernes' : 'Monday, Wednesday, Friday',
		action: () => closeDrawer(),
		waitFor: () => !isReportDrawerOpen.value,
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// TABLE SECTION
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 20: Rating Reports Table ────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Reports Rating List') || findByText('Reports rating List')
			if (el) {
				const card = el.closest('.MuiPaper-root') || el.closest('.MuiCard-root')
				if (card) return card
			}
			return document.querySelector('.MuiTable-root')?.closest('.MuiPaper-root')
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de Informes Rating' : 'Rating Reports Table',
		description: ES
			? 'Todos tus informes de rating listados aqui. Cada fila muestra la configuracion del informe incluyendo las oficinas monitorizadas, los sitios directos, la programacion y el estado de ejecucion actual.'
			: 'All your rating reports listed here. Each row shows the report configuration including monitored offices, direct sites, schedule, and current execution status.',
	},
	// ─── 21: Report Name column ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Report name'),
		icon: 'tag',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre de cada informe rating. Usa nombres claros que incluyan la oficina o mercado (p. ej., "Madrid Airport Rating Weekly") para identificar rapidamente los informes en la lista.'
			: 'The name of each rating report. Use clear naming that includes the office or market (e.g., "Madrid Airport Rating Weekly") to quickly identify reports in the list.',
	},
	// ─── 22: Search numbers column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Search numbers') || findTableColumn('Searches'),
		icon: 'hashtag',
		title: ES ? 'Numero de Busquedas' : 'Search Numbers',
		description: ES
			? 'Total de busquedas por ejecucion. Los informes con mas de 1.000 busquedas muestran un icono de fuego como advertencia. Un numero alto de busquedas puede aumentar el tiempo de ejecucion.'
			: 'Total searches per execution. Reports with 1,000+ searches show a fire icon warning. A high number of searches may increase execution time.',
		example: ES
			? 'Menos de 1K = Normal | 1K+ = Advertencia de fuego'
			: 'Under 1K = Normal | 1K+ = Fire warning',
	},
	// ─── 23: Sites column ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Sites'),
		icon: 'globe',
		title: ES ? 'Sitios' : 'Sites',
		description: ES
			? 'Los sitios directos monitorizados para el analisis de rating. El rating compara tus precios frente a los de estos sitios para determinar tu posicion competitiva.'
			: 'The direct sites being monitored for rating analysis. The rating compares your prices against these sites to determine your competitive position.',
	},
	// ─── 24: Frequency Hours column ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency Hours') || findTableColumn('Hours'),
		icon: 'clock',
		title: ES ? 'Horas de Frecuencia' : 'Frequency Hours',
		description: ES
			? 'Horas de ejecucion programadas para este informe de rating. Determina con que frecuencia diaria se actualizan los datos de rating.'
			: 'Scheduled execution times for this rating report. Determines how frequently the rating data is updated daily.',
	},
	// ─── 25: Frequency Days column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency'),
		icon: 'calendar-days',
		title: ES ? 'Dias de Frecuencia' : 'Frequency Days',
		description: ES
			? 'Dias de la semana en los que se ejecuta el informe de rating. Combinado con las horas, define el calendario completo de monitorizacion.'
			: 'Days of the week when the rating report executes. Combined with the hours, this defines the complete monitoring schedule.',
	},
	// ─── 26: Airports / Offices column ──────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Pick up') || findTableColumn('Airports'),
		icon: 'location-dot',
		title: ES ? 'Oficinas Monitorizadas' : 'Monitored Offices',
		description: ES
			? 'Las oficinas de recogida (y devolucion si aplica) incluidas en el analisis de rating. El rating se calcula para cada combinacion de oficina y sitio directo monitorizados.'
			: 'The pickup offices (and dropoff if applicable) included in the rating analysis. The rating is calculated for each combination of office and monitored direct site.',
	},
	// ─── 27: Date & Execution Status column ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Date'),
		icon: 'calendar-check',
		title: ES ? 'Fecha y Estado de Ejecucion' : 'Date & Execution Status',
		description: ES
			? 'Ultima fecha de ejecucion y estado: Listo (completado, acceso al Rating Analyzer disponible), En ejecucion (barra de progreso con contador de tareas) o Detenido. Haz clic en la barra de progreso durante la ejecucion para ver los detalles.'
			: 'Last execution date and status: Ready (completed, Rating Analyzer access available), Running (progress bar with task count), or Stopped. Click the progress bar during execution to view details.',
		example: ES
			? 'Listo = Rating Analyzer | En ejecucion = Progreso | Detenido = Parado'
			: 'Ready = Rating Analyzer | Running = Progress | Stopped = Halted',
	},
	// ─── 28: Rating Analyzer button ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-rating"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-rating"]'),
		icon: 'star',
		title: ES ? 'Rating Analyzer' : 'Rating Analyzer',
		description: ES
			? 'El icono estrella abre el Rating Analyzer con los datos de la ultima ejecucion. Disponible solo cuando el estado es "Listo". El Rating Analyzer muestra tu posicion de precios frente a la competencia para cada combinacion de oficina, sitio y duracion.'
			: 'The star icon opens the Rating Analyzer with the latest execution data. Available only when the status is "Ready". The Rating Analyzer shows your price position against competitors for each office, site, and duration combination.',
	},
	// ─── 29: RealTime Historical button ──────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-realtime"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-realtime"]'),
		icon: 'clock-rotate-left',
		title: ES ? 'Historico en Tiempo Real' : 'Real Time Historical',
		description: ES
			? 'El icono de reloj navega al historico en tiempo real filtrado por este informe. Permite ver la evolucion de los precios a lo largo del tiempo para analizar tendencias y variaciones estacionales.'
			: 'The clock icon navigates to the real-time historical filtered by this report. Allows viewing price evolution over time to analyze trends and seasonal variations.',
	},
	// ─── 30: Enable / Disable toggle ────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const sw = document.querySelector('.MuiSwitch-root')
			if (sw) return sw.closest('td') || sw
			return findTableColumn('Off/On') || findTableColumn('On')
		},
		icon: 'toggle-on',
		title: ES ? 'Activar / Desactivar' : 'Enable / Disable',
		description: ES
			? 'Activa o desactiva un informe. Los informes desactivados conservan su configuracion pero no se ejecutaran segun la programacion. Util para pausar la monitorizacion de rating temporalmente.'
			: 'Toggle a report on or off. Disabled reports keep their configuration but won\'t execute on schedule. Useful for temporarily pausing rating monitoring.',
	},
	// ─── 31: Row Actions ────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Actions'),
		icon: 'gears',
		title: ES ? 'Acciones de Fila' : 'Row Actions',
		description: ES
			? 'Acciones por informe: Ejecutar (ejecutar ahora), Editar (cambiar configuracion), Clonar (duplicar), Info (detalles e historial) y Eliminar. Las acciones se desactivan mientras un informe esta en ejecucion.'
			: 'Actions per report: Perform (run now), Edit (change config), Clone (duplicate), Info (details & history), and Delete. Actions are disabled while a report is running.',
		example: ES
			? 'Ejecutar | Editar | Clonar | Info | Eliminar'
			: 'Perform | Edit | Clone | Info | Delete',
	},
	// ─── 32: Show Report Info button ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-show-report-info"]') ||
			document.querySelector('[title="Show Report Info"]')
		),
		skipIf: () => !reportListData.value.length,
		icon: 'circle-info',
		title: ES ? 'Boton Informacion del Informe' : 'Show Report Info Button',
		description: ES
			? 'El boton "i" en la columna Acciones abre el panel de detalles completos del informe. Desde ahi puedes consultar toda la configuracion guardada y el historial de ejecuciones con sus resultados.'
			: 'The "i" button in the Actions column opens the full report details panel. From there you can view the complete saved configuration and the execution history with its results.',
		action: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		waitFor: () => !!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root'),
	},
	// ─── 33: Info Modal — overview ───────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('.MuiDialog-root'),
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-list',
		title: ES ? 'Panel de Informacion del Informe' : 'Report Info Panel',
		description: ES
			? 'Este modal muestra toda la informacion del informe en dos pestañas: "Report Details" con la configuracion completa guardada, y "Report History" con el historial de ejecuciones, sus estados y acciones de descarga. Util para auditar cambios o recuperar resultados anteriores.'
			: 'This modal shows all report information in two tabs: "Report Details" with the complete saved configuration, and "Report History" with the execution history, their statuses, and download actions. Useful for auditing changes or retrieving previous results.',
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 34: Info Modal — Tab Report Details ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('details')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'memo-circle-info',
		title: ES ? 'Pestaña Report Details' : 'Report Details Tab',
		description: ES
			? 'La pestaña "Report Details" muestra la configuracion completa tal como fue guardada: nombre, sitios directos, oficinas, moneda, fechas de busqueda, duraciones, horas y dias de ejecucion, y emails de notificacion.'
			: 'The "Report Details" tab shows the complete configuration as saved: name, direct sites, offices, currency, search dates, durations, execution hours and days, and notification emails.',
		action: () => { selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS },
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 35: Info Modal — Tab Report History ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('history')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-history',
		title: ES ? 'Pestaña Report History' : 'Report History Tab',
		description: ES
			? 'La pestaña "Report History" muestra todas las ejecuciones anteriores del informe en orden cronologico inverso. Para cada ejecucion ves la fecha, el estado (Listo, Detenido) y las acciones disponibles. Perfecto para recuperar resultados de ejecuciones pasadas.'
			: 'The "Report History" tab shows all previous executions of the report in reverse chronological order. For each execution you see the date, the status (Ready, Stopped), and available actions. Perfect for retrieving results from past executions.',
		action: () => {
			selectedInfoModalTab.value = INFO_MODAL_TABS.HISTORY
			isOpenInfoModal.value = null
		},
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 36: Multi-Select ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const checkboxes = document.querySelectorAll('th .MuiCheckbox-root, th input[type="checkbox"]')
			if (checkboxes.length) return checkboxes[0].closest('th')
			return null
		},
		icon: 'list-check',
		title: ES ? 'Seleccion Multiple' : 'Multi-Select',
		description: ES
			? 'Selecciona varios informes rating para aplicar acciones masivas: editar sitios, recogidas, emails, frecuencia, ejecutar, detener, copiar, eliminar, activar o desactivar — todo a la vez.'
			: 'Select multiple rating reports to apply bulk actions: edit sites, pickups, emails, frequency, perform, stop, copy, delete, enable, or disable — all at once.',
		example: ES
			? 'Selecciona filas → Barra de acciones masivas'
			: 'Select rows → Bulk toolbar with mass actions',
	},
	// ─── 37: Outro ──────────────────────────────────────────────────────────────
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Crea tu primer informe rating para empezar a monitorizar tu posicion de precios frente a la competencia. Usa el Rating Analyzer para analizar los resultados y el boton Info para consultar el historial de ejecucion.'
			: 'Create your first rating report to start monitoring your price position against competitors. Use the Rating Analyzer to analyze results and the Info button to check execution history.',
	},
]
